/**
 * Guest RSVP capability tokens.
 *
 * A guest who books without an account gets a high-entropy token that is the credential for
 * managing (view/edit/cancel/pay) that specific RSVP. Only the SHA-256 hash of the token is
 * stored on the RSVP document (`manageTokenHash`); the raw token lives only in the guest's link
 * and short-lived session storage. Server callables (getGuestRsvp / manageGuestRsvp /
 * cancelGuestRsvp) verify a presented token by re-hashing it and comparing to the stored hash.
 *
 * The hash encoding (lowercase hex of SHA-256) matches the backend's Node `crypto` implementation
 * in backend/functions/src/events/guestRsvpAccess.ts.
 */

/** Generate a random, URL-safe capability token (~192 bits of entropy). */
export function generateGuestRsvpToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  // base64url without padding
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Lowercase hex SHA-256 of the token, matching the backend hashing. */
export async function hashGuestRsvpToken(token: string): Promise<string> {
  const data = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const sessionKey = (rsvpId: string) => `guest_rsvp_token_${rsvpId}`;

/** Persist the raw token for the current browser session so the guest can manage their RSVP. */
export function storeGuestRsvpToken(rsvpId: string, token: string): void {
  try {
    sessionStorage.setItem(sessionKey(rsvpId), token);
  } catch {
    /* sessionStorage unavailable — the token in the URL still works */
  }
}

/** Read a previously stored raw token for this RSVP, if any. */
export function readStoredGuestRsvpToken(rsvpId: string): string | null {
  try {
    return sessionStorage.getItem(sessionKey(rsvpId));
  } catch {
    return null;
  }
}
